import { Chip } from '../../atoms/chip/chip.js';
import type { CaseStatus } from '../../status-colors.js';

/**
 * StatusChip — the case status pill.
 *
 * Corpus: `DB:1841`, the `chip(fg, bg)` factory — 11.5px/700 on a
 * pill, 4px 11px padding, no wrapping — and `DB:2177-2180`
 * (`allStatusChips`), where the eight statuses are drawn on five colour pairs.
 * The set is rendered as the status legend at `DB:1442-1447`.
 *
 * A molecule rather than an atom. The pill shape was `.rp-chip`,
 * declared here and rendered by SeverityChip too — a shared base with two
 * renderers and one owner, which the tier convention has no slot for. `Chip` owns
 * the geometry now, and the five `TONES` at `DB:2555-2559`;
 * composing that atom is what makes this a molecule. It also closes the one
 * departure this file carried from the factory it cites: the text was
 * `--rp-text-2xs` (10.5px) where the corpus says 11.5px.
 *
 * The tone map below restates a corpus table that `status-colors.ts` also holds,
 * so `status-chip.test.tsx` asserts the two agree tone by tone against
 * `tokens.css` rather than leaving a second copy to drift. The value export
 * stays: `contrast.test.ts` recomputes it, and a status is drawn in places that
 * are not a chip.
 *
 * The label is a prop, never a literal inside this file: every string a person
 * reads is supplied by the caller from a translation catalogue.
 */
export interface StatusChipProps {
  readonly status: CaseStatus;
  readonly label: string;
}

/** `DB:2177-2180` — eight statuses across five of the tones. */
export const statusTones = {
  open: 'green',
  acknowledged: 'green',
  scheduled: 'slate',
  awaiting_quote: 'slate',
  quoted: 'slate',
  overdue: 'red',
  escalated: 'amber',
  resolved: 'grey',
} as const satisfies Record<CaseStatus, 'green' | 'red' | 'amber' | 'slate' | 'grey'>;

/**
 * Text carries the meaning; colour reinforces it (never colour-alone).
 *
 * Which is why a blank label renders nothing at all — `Chip` refuses it, for the
 * reason this component first wrote down. The pill used to be drawn regardless,
 * on the reasoning that an empty pill reads as the missing string it is; what it
 * actually reads as is a coloured shape, which is the whole of what a sighted
 * reader gets while a screen-reader user hears silence either way. The component
 * may not supply a fallback word, because a word in here is copy and copy comes
 * from the caller's catalogue, so unknown degrades to silence
 * (the jurisdiction rule) and the missing key is caught by the catalogue.
 */
export function StatusChip({ status, label }: StatusChipProps) {
  return <Chip label={label} tone={statusTones[status]} />;
}
